import { FC, useContext, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { FormGenerator, FormInputProps } from '@microservices/wiskey-react-components'
import { Close, Done } from '@mui/icons-material'
import { Grid, IconButton, SxProps } from '@mui/material'

import {
  getBackgroundByElementType,
  getEditElementValueByElementType,
} from '@pages/ControlPanelsCreateOrEdit/helpers'

import { CommandParameters } from '@components/CommandParameters'
import { getStateFromData } from '@components/CommandParameters/helpers'
import { getDataFromState } from '@components/CommandParameters/helpers/getDataFromState'

import {
  useFetchAllFormQuery,
  useFetchAllGanttQuery,
  useFetchAllViewQuery,
  useFetchMapsConfigsQuery,
} from '@redux/api'
import { useFetchCachedBarChartsQuery } from '@redux/api/barChart.api'
import { useFetchAllTimeLinesQuery } from '@redux/api/timeline.api'
import { useFetchAllWindowsQuery } from '@redux/api/windows.api'

import { useCommands, useCommandSources } from '@hooks'
import { getEntityAutocompleteOptionsFromType, isSystemSource } from '@helpers'
import {
  ALIGNMENTS,
  ELEMENT_TYPE,
  ELEMENT_TYPE_OPTIONS,
  ENTITY_TYPES_REQUEST,
  GENERATOR_INPUT_TYPE,
  MENU_TYPE,
  MENU_TYPE_OPTIONS,
  REGEX,
  selectTypeFields,
  SOURCE_SELECTION_OPTIONS,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import {
  AutocompleteOption,
  CommandParametersType,
  CommandType,
  TreeItemControlPanelOptionType,
} from '@types'

import { ALIGNMENTS_OPTIONS } from '../../../../constants/alignments'
import { MenuTabPanelContext } from '../MenuTabPanel'

type AddItemProps = {
  treeItem?: TreeItemControlPanelOptionType
  onClose?: () => void
  isEdit?: boolean
  sx?: SxProps
}

type FormValues = {
  name: string
  menuType: (typeof MENU_TYPE_OPTIONS)[number] | null
  elementType: (typeof ELEMENT_TYPE_OPTIONS)[number] | null
  sourceType: AutocompleteOption<TYPES_SOURCE_OPTIONS> | null
  element: AutocompleteOption<number | string> | null
  alignment: string | undefined
  background: boolean | null
  command?: CommandType | null
  object?: AutocompleteOption | null
  commandParameters?: CommandParametersType[]
  commandBody?: CommandParametersType[]
}

export const AddItem: FC<AddItemProps> = ({ sx, onClose, treeItem, isEdit }) => {
  const { t } = useTranslation()
  const { tree, isDirty, handleAddTreeItem, handleEditTreeItem } = useContext(MenuTabPanelContext)

  const defaultValues: FormValues = {
    menuType: null,
    elementType: null,
    sourceType: {
      id: TYPES_SOURCE_OPTIONS.OBJECT,
      label: TYPES_SOURCE_OPTIONS.OBJECT,
    },
    name: '',
    element: null,
    background: null,
    alignment: ALIGNMENTS.LEFT,
    object: null,
    command: null,
    commandParameters: [],
    commandBody: [],
  }
  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  })
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty: itemIsDirty },
  } = methods

  const currentElementType = watch('elementType', null)
  const currentElement = watch('element', null)
  const currentMenuType = watch('menuType', null)
  const currentObject = watch('object')
  const isDivider = currentMenuType?.id === MENU_TYPE.DIVIDER
  const watchedSourceType = watch('sourceType')

  const disabledSelectObject =
    currentElementType?.id === ELEMENT_TYPE.GROUP ||
    currentElementType?.id === ELEMENT_TYPE.SEARCH_ASSISTANT ||
    isDivider

  const entityTypeForRequest = useMemo(() => {
    if (!currentElementType) {
      return undefined
    }

    switch (currentElementType.id) {
      case ELEMENT_TYPE.VIEW:
        return ENTITY_TYPES_REQUEST.VIEW
      case ELEMENT_TYPE.GANTT_CHART:
        return ENTITY_TYPES_REQUEST.GANTT_CHART
      default:
        return undefined
    }
  }, [currentElementType])

  const {
    data: entities,
    isLoading: isLoadingEntities,
    isFetching: isFetchingEntities,
  } = useFetchAllViewQuery({ viewType: entityTypeForRequest }, { skip: !entityTypeForRequest })

  const {
    data: forms,
    isLoading: isLoadingForms,
    isFetching: isFetchingForms,
  } = useFetchAllFormQuery(undefined, { skip: currentElementType?.id !== ELEMENT_TYPE.FORM })

  const { commandOptions, isLoadingCommandOptions } = useCommands({
    sourceCommand: currentObject?.id,
    currentSourceType: watchedSourceType?.label as TYPES_SOURCE_OPTIONS,
  })

  const {
    objectsOptions,
    systemsOptions,
    loadingObjects: isLoadingObjects,
    loadingSystems: isLoadingSystems,
  } = useCommandSources()

  const optionsForSourceCommand = isSystemSource(watchedSourceType?.label)
    ? systemsOptions
    : objectsOptions

  const allOptionsForSourceCommand = [...systemsOptions, ...objectsOptions]

  const { data: timetables, isLoading: isLoadingTimetables } = useFetchAllTimeLinesQuery(
    undefined,
    {
      skip: currentElementType?.id !== ELEMENT_TYPE.TIME_TABLE && !currentElement,
    }
  )

  const { data: mapsConfigs, isLoading: isLoadingMapsConfigs } = useFetchMapsConfigsQuery(
    undefined,
    {
      skip: currentElementType?.id !== ELEMENT_TYPE.MAP_CONFIG && !currentElement,
    }
  )

  const { data: barCharts, isLoading: isLoadingBarCharts } = useFetchCachedBarChartsQuery(
    undefined,
    {
      skip: currentElementType?.id !== ELEMENT_TYPE.BAR_CHART && !currentElement,
    }
  )

  const { data: ganttConfigList } = useFetchAllGanttQuery({}, {})

  const { data: windows } = useFetchAllWindowsQuery({})

  const loadingObjects =
    isLoadingEntities ||
    isFetchingEntities ||
    isLoadingForms ||
    isFetchingForms ||
    isLoadingCommandOptions ||
    isLoadingTimetables ||
    isLoadingBarCharts ||
    isLoadingMapsConfigs

  const treeNames = useMemo(
    () => Object.values(tree?.items ?? {}).map(op => op.data.title.toLowerCase()),
    [tree]
  )

  const isCurrentTreeName = (value: string) =>
    Boolean(isEdit && treeItem && treeItem.data.title.toLowerCase() === value)

  const autocompleteViewOptions = useMemo(
    () =>
      getEntityAutocompleteOptionsFromType(currentElementType?.id, {
        [ELEMENT_TYPE.VIEW]: entities?.data,
        [ELEMENT_TYPE.GANTT_CHART]: ganttConfigList,
        [ELEMENT_TYPE.FORM]: forms?.data,
        [ELEMENT_TYPE.COMMAND]: commandOptions,
        [ELEMENT_TYPE.TIME_TABLE]: timetables,
        [ELEMENT_TYPE.BAR_CHART]: barCharts,
        [ELEMENT_TYPE.WINDOW]: windows,
        [ELEMENT_TYPE.MAP_CONFIG]: mapsConfigs,
      }),
    [
      currentElementType?.id,
      entities?.data,
      forms?.data,
      commandOptions,
      timetables,
      barCharts,
      windows,
      mapsConfigs,
    ]
  )

  useEffect(() => {
    if (!isDirty && !isEdit) {
      reset()
    }
  }, [tree])

  useEffect(() => {
    if (isEdit) {
      reset({
        menuType: MENU_TYPE_OPTIONS.find(({ label }) => label === treeItem?.data.menuType),
        name: treeItem?.data.title,
        element: getEditElementValueByElementType(
          treeItem?.data.elementType,
          treeItem?.data.elementId,
          treeItem?.data.elementTitle
        ),
        elementType: ELEMENT_TYPE_OPTIONS.find(({ id }) => id === treeItem?.data.elementType),
        background: treeItem?.data.background,
        alignment: treeItem?.data.alignment,
        sourceType: {
          id: treeItem?.data.command?.resource,
          label: treeItem?.data.command?.resource,
        },
        object:
          treeItem?.data.elementType === ELEMENT_TYPE.COMMAND
            ? allOptionsForSourceCommand.find(opt => opt.id === treeItem?.data.elementCode) ?? null
            : null,
        command: treeItem?.data.command,
        commandParameters: getStateFromData(treeItem?.data.commandParameters),
        commandBody: getStateFromData(treeItem?.data.commandBody),
      })

      return
    }
    reset(defaultValues)
  }, [isEdit])

  const onAdd = (data: FormValues): void => {
    const {
      name,
      element,
      elementType,
      menuType,
      background,
      alignment,
      commandParameters,
      commandBody,
      object,
      command,
    } = data

    if ((elementType && menuType) || menuType?.id === MENU_TYPE.DIVIDER) {
      const option = {
        title: name,
        elementCode:
          elementType?.id === ELEMENT_TYPE.COMMAND ? object?.code : currentElement?.code || null,
        elementTitle: element?.label || null,
        elementId: elementType?.id !== ELEMENT_TYPE.COMMAND ? element?.id || null : null,
        elementType: elementType?.id || null,
        menuType: menuType?.id || null,
        alignment: alignment || undefined,
        background: getBackgroundByElementType(background, elementType?.id),
        command: command?.objectCode
          ? {
              ...command,
              resource: watchedSourceType?.id,
            }
          : null,
        commandParameters: getDataFromState(commandParameters),
        commandBody: getDataFromState(commandBody),
      }

      if (isEdit && treeItem) {
        handleEditTreeItem({
          ...treeItem,
          data: {
            ...treeItem.data,
            ...option,
          },
        })

        onClose?.()

        return
      }

      handleAddTreeItem(
        {
          uuid: uuid(),
          hasChild: false,
          childrenMenu: [],
          ...(menuType?.label === MENU_TYPE.ICON && { showTitle: true, iconName: null }),
          ...(treeItem?.data.id
            ? { parentId: treeItem.data.id }
            : { parentUuid: treeItem?.data.uuid }),
          ...option,
        },
        treeItem
      )

      reset()
      onClose?.()

      return
    }
  }

  const handleClose = () => {
    if (treeItem) {
      onClose?.()
    }

    reset()
  }

  const handleSelectTypeChange = (e: any) => {
    if (e.target.value) {
      if (currentElementType?.id !== e.target.value.id) {
        setValue('element', null, { shouldValidate: true })
      }

      return
    }

    setValue('element', null, { shouldValidate: true })
  }

  const clearParamsAndBody = () => {
    setValue('commandParameters', [])
    setValue('commandBody', [])
  }

  const handleChangeSourceType = (value: AutocompleteOption) => {
    if (watchedSourceType?.id === value?.id) return

    setValue('object', null)
    setValue('element', null)

    clearParamsAndBody()
  }

  const handleChangeSourceCommand = (value: AutocompleteOption) => {
    if (currentObject?.id === value?.id) return

    setValue('element', null)

    clearParamsAndBody()
  }

  const handleChangeElement = (value: AutocompleteOption) => {
    if (currentElementType?.id !== ELEMENT_TYPE.COMMAND) {
      return
    }

    if (value?.label) {
      const command = commandOptions?.find(cmd => cmd.label === value.label)

      setValue('command', {
        id: null,
        name: command?.label,
        httpMethod: command?.httpMethod,
        objectCode: currentObject?.id,
      })
    }

    clearParamsAndBody()
  }

  const isNameUsed = (value: string) =>
    !treeNames.includes(value.toLowerCase()) || isCurrentTreeName(value.toLowerCase())

  const checkValidate = (value: string): string | undefined => {
    if (!(value.trim().length !== 0 && isNameUsed(value))) {
      return t('error.alreadyUsed', { name: value })
    }
    if (value.trim().length === 0) return t('error.onlyWhitespacesTitleInput')
    if (!REGEX.CHECK_LETTERS_DASH_UNDERLINE_WHITESPACE_NUMBERS.test(value))
      return t('error.notValidSymbolsTitleInput')
  }

  return (
    <Grid
      container
      item
      alignItems='flex-start'
      direction={'row'}
      spacing={1}
      sx={sx}
      width={'100%'}
    >
      <FormProvider {...methods}>
        <Grid item flex={1}>
          <FormGenerator
            mainGridSpacing={0}
            inputs={[
              {
                rows: 'row',
                name: 'row',
                inputs: [
                  {
                    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                    name: 'menuType',
                    placeholder: t(`controlPanelCreate.form.addItem.pointType.placeholder`),
                    autocompleteOptions: MENU_TYPE_OPTIONS,
                    rules: {
                      required: true,
                    },
                  },
                  {
                    inputType: GENERATOR_INPUT_TYPE.INPUT,
                    name: 'name',
                    placeholder: t(`controlPanelCreate.form.addItem.name.placeholder`),
                    maxLengthInput: 100,
                    rules: {
                      required: true,
                      maxLength: 100,
                      validate: value => checkValidate(value),
                    },
                  },
                  {
                    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                    name: 'elementType',
                    disabled: (isEdit && treeItem?.hasChildren) || isDivider,
                    placeholder: t(`controlPanelCreate.form.addItem.entityType.placeholder`),
                    loading: !selectTypeFields,
                    isOptionEqualToValue: (option, value) => option.label === value.label,
                    autocompleteOptions: ELEMENT_TYPE_OPTIONS,
                    rules: {
                      onChange: handleSelectTypeChange,
                    },
                  },
                  ...(currentElementType?.id === ELEMENT_TYPE.COMMAND
                    ? [
                        {
                          name: 'sourceType',
                          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                          labelPlacement: 'end',
                          disableClearable: true,
                          placeholder: t('placeholder.selectSourceType'),
                          autocompleteOptions: SOURCE_SELECTION_OPTIONS,
                          onChangeAutocomplete: handleChangeSourceType,
                        } as FormInputProps,
                        {
                          name: 'object',
                          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                          labelPlacement: 'end',
                          placeholder: t(`placeholder.selectSourceCommand`),
                          loading: isLoadingObjects || isLoadingSystems,
                          autocompleteOptions: optionsForSourceCommand,
                          onChangeAutocomplete: handleChangeSourceCommand,
                        } as FormInputProps,
                      ]
                    : []),
                  {
                    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                    name: 'element',
                    disabled:
                      (isEdit && treeItem?.hasChildren) ||
                      !currentElementType ||
                      disabledSelectObject ||
                      currentElementType?.id === ELEMENT_TYPE.FILE_UPLOAD ||
                      (currentElementType?.id === ELEMENT_TYPE.COMMAND && !currentObject),
                    placeholder:
                      currentElementType?.id === ELEMENT_TYPE.COMMAND
                        ? t(`placeholder.selectCommandCode`)
                        : t(`controlPanelCreate.form.addItem.entity.placeholder`),
                    loading: loadingObjects,
                    isOptionEqualToValue: (option, value) => option.label === value.label,
                    autocompleteOptions: autocompleteViewOptions,
                    onChangeAutocomplete: (value: AutocompleteOption) => handleChangeElement(value),
                  },
                  {
                    inputType: GENERATOR_INPUT_TYPE.SELECT,
                    name: 'alignment',
                    placeholder: t(`controlPanelCreate.form.addItem.entity.placeholder`),
                    selectOptions: ALIGNMENTS_OPTIONS,
                  },
                  ...(currentElementType?.id === ELEMENT_TYPE.VIEW && currentElement
                    ? [
                        {
                          name: 'background',
                          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                          label: t(`controlPanelCreate.form.addItem.background`),
                          labelPlacement: 'end',
                        } as FormInputProps,
                      ]
                    : []),
                ],
              },
            ]}
          />
          {currentElementType?.id === ELEMENT_TYPE.COMMAND && currentElement && (
            <>
              <CommandParameters
                commandCode={currentElement?.label as string}
                containerSx={{ mt: 2 }}
                name='commandParameters'
                parametersMode='params'
                sourceCode={(currentObject?.id as string) ?? ''}
                sourceTitle={currentObject?.label ?? ''}
                typeSource={watchedSourceType?.id}
              />
              <CommandParameters
                commandCode={currentElement?.label as string}
                name='commandBody'
                parametersMode='body'
                sourceCode={(currentObject?.id as string) ?? ''}
                sourceTitle={currentObject?.label ?? ''}
                typeSource={watchedSourceType?.id}
              />
            </>
          )}
        </Grid>

        <Grid item>
          <IconButton disabled={!itemIsDirty} onClick={handleSubmit(onAdd)}>
            <Done color={itemIsDirty ? 'primary' : 'disabled'} />
          </IconButton>
          <IconButton onClick={handleClose}>
            <Close color='error' />
          </IconButton>
        </Grid>
      </FormProvider>
    </Grid>
  )
}
